/**
 * Mobile nav drawer: strip toggles (localStorage), drill-in panels (stacked), STORES link closes drawer.
 * Relies on <header-drawer>.close() from assets/header-drawer.js
 */
(function () {
  const STRIP_MS = 280;

  /** @returns {boolean} */
  function prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }

  /** Nav block option: force instant strip + drill motion for all visitors. */
  function instantMotionFromContext(el) {
    if (!(el instanceof Element)) return false;
    const mobile = el.closest('.menu-list__mobile');
    if (mobile?.getAttribute('data-nav-motion') === 'instant') return true;
    const stack = el.closest('[data-nav-drill-stack]');
    if (stack?.getAttribute('data-nav-motion') === 'instant') return true;
    return false;
  }

  /**
   * Move drill stack hosts from .menu-list__mobile to nav.menu-drawer__navigation so
   * position:absolute; inset:0 covers the scrollport (avoids inline panel flow under list rows).
   */
  function hoistDrillStacks(root) {
    const from = root instanceof Document ? document.body : root;
    if (!(from instanceof HTMLElement)) return;
    for (const stack of from.querySelectorAll('[data-nav-drill-stack].menu-drawer-drill-stack')) {
      if (!(stack instanceof HTMLElement) || !stack.id) continue;
      const nav = stack.closest('.menu-drawer__navigation');
      if (!nav) continue;
      if (stack.parentElement === nav) continue;
      const mobile = stack.closest('.menu-list__mobile');
      if (!mobile) continue;
      stack.setAttribute('data-nav-motion', mobile.getAttribute('data-nav-motion') || 'system');
      const menuId = mobile.dataset.menuId?.trim();
      if (menuId) stack.setAttribute('data-nav-drill-menu-id', menuId);
      else stack.removeAttribute('data-nav-drill-menu-id');
      nav.appendChild(stack);
    }
    syncHoistedDrillStackVisibility();
  }

  /** Match hoisted stacks to body[data-active-brand] (runs after brands.js on first paint, and on re-init). */
  function syncHoistedDrillStackVisibility() {
    const active = document.body?.dataset?.activeBrand?.trim().toLowerCase();
    if (!active) return;
    document.querySelectorAll('[data-nav-drill-stack][data-nav-drill-menu-id]').forEach((node) => {
      const id = node.dataset.navDrillMenuId?.trim().toLowerCase();
      if (!id) return;
      const inactive = id !== active;
      node.classList.toggle('hidden', inactive);
      if (inactive) node.setAttribute('hidden', '');
      else node.removeAttribute('hidden');
    });
  }

  /** @returns {HTMLElement | null} */
  function getHeaderDrawer() {
    return document.querySelector('header-drawer');
  }

  function closeHeaderDrawer() {
    const el = getHeaderDrawer();
    if (el && typeof el.close === 'function') el.close();
  }

  function focusMenuSummary() {
    const details = document.getElementById('Details-menu-drawer-container');
    const summary = details?.querySelector('summary');
    if (summary instanceof HTMLElement) summary.focus();
  }

  /** @param {Document | HTMLElement} root */
  function initStripRows(root) {
    for (const row of root.querySelectorAll('[data-nav-drawer-strip-row]')) {
      if (row.dataset.navStripInit === '1') continue;
      row.dataset.navStripInit = '1';

      const btn = row.querySelector('.nav-drawer-strip-row__toggle');
      const region = row.querySelector('[data-nav-drawer-strip-region]');
      const key = row.dataset.stripStorageKey?.trim();
      const chev = row.querySelector('.nav-drawer-strip-row__chev');
      if (!(btn instanceof HTMLButtonElement) || !(region instanceof HTMLElement) || !key) continue;

      const forceInstant = instantMotionFromContext(row);
      const reduced = prefersReducedMotion() || forceInstant;

      const merchantDefaultOpen = row.dataset.stripDefaultExpanded !== 'false';
      /** Scope saved toggle state to the theme “open by default” setting so turning it off is not overridden by an old key. */
      const storageKey = `${key}__default_${merchantDefaultOpen ? 'open' : 'closed'}`;

      function readExpanded() {
        try {
          const v = localStorage.getItem(storageKey);
          if (v == null) return merchantDefaultOpen;
          return v === 'true';
        } catch {
          return merchantDefaultOpen;
        }
      }

      function writeExpanded(open) {
        try {
          localStorage.setItem(storageKey, open ? 'true' : 'false');
        } catch {
          /* ignore */
        }
      }

      /** @param {boolean} open */
      function apply(open) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        row.classList.toggle('nav-drawer-strip-row--collapsed', !open);
        if (chev) chev.classList.toggle('nav-drawer-strip-row__chev--collapsed', !open);
        region.classList.toggle('nav-drawer-strip-row__region--collapsed', !open);
        if (reduced) {
          row.classList.add('nav-drawer-strip-row--reduced-motion');
        } else {
          row.classList.remove('nav-drawer-strip-row--reduced-motion');
        }
      }

      apply(readExpanded());

      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        writeExpanded(open);
        apply(open);
        if (open) {
          requestAnimationFrame(() => {
            for (const track of region.querySelectorAll('.mega-menu-product-strip__track-outer')) {
              if (track instanceof HTMLElement) syncStripTrack(track);
            }
          });
        }
      });
    }
  }

  /** @type {WeakMap<HTMLElement, DrillStack>} */
  const drillStacks = new WeakMap();

  class DrillStack {
    /** @param {HTMLElement} stackEl */
    constructor(stackEl) {
      this.stack = stackEl;
      /** @type {HTMLElement | null} */
      this.panelsRoot = stackEl.querySelector('[data-nav-drill-panels]');
      /** @type {{ el: HTMLElement, opener: HTMLElement | null }[]} */
      this.chain = [];
      this._onDocKeyup = this._onDocKeyup.bind(this);
    }

    shouldUseInstant() {
      return prefersReducedMotion() || instantMotionFromContext(this.stack);
    }

    get depth() {
      return this.chain.length;
    }

    _activate() {
      this.stack.hidden = false;
      this.stack.classList.remove('hidden');
      this.stack.removeAttribute('hidden');
      this.stack.setAttribute('aria-hidden', 'false');
      this.stack.dataset.navDrillActive = '1';
      document.addEventListener('keyup', this._onDocKeyup, true);
    }

    _deactivateIfEmpty() {
      if (this.depth > 0) return;
      this.stack.hidden = true;
      this.stack.setAttribute('aria-hidden', 'true');
      delete this.stack.dataset.navDrillActive;
      document.removeEventListener('keyup', this._onDocKeyup, true);
    }

    /** @param {KeyboardEvent} e */
    _onDocKeyup(e) {
      if (e.key !== 'Escape') return;
      if (this.depth === 0) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      this.pop();
    }

    /**
     * @param {string} title
     * @param {DocumentFragment} bodyFragment
     * @param {HTMLElement | null} opener
     */
    push(title, bodyFragment, opener) {
      if (!this.panelsRoot) return;
      this._activate();

      const panel = document.createElement('div');
      panel.className = 'menu-drawer-drill-panel';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.innerHTML = [
        '<header class="menu-drawer-drill-panel__header">',
        '<button type="button" class="menu-drawer-drill-panel__icon-btn" data-nav-drill-back aria-label="',
        'Back',
        '">',
        '<span class="svg-wrapper" aria-hidden="true"></span>',
        '</button>',
        '<h2 class="menu-drawer-drill-panel__title"></h2>',
        '</header>',
        '<div class="menu-drawer-drill-panel__body" data-nav-drill-panel-body></div>',
      ].join('');

      const titleEl = panel.querySelector('.menu-drawer-drill-panel__title');
      if (titleEl) titleEl.textContent = title;

      const backSlot = panel.querySelector('[data-nav-drill-back] .svg-wrapper');
      if (backSlot) backSlot.innerHTML = window.__navDrawerLucideChevronLeft || '';

      const body = panel.querySelector('[data-nav-drill-panel-body]');
      if (body) body.appendChild(bodyFragment);

      const z = 300 + this.depth;
      panel.style.zIndex = String(z);

      const backBtn = panel.querySelector('[data-nav-drill-back]');

      backBtn?.addEventListener('click', () => this.pop());

      this.panelsRoot.appendChild(panel);
      this.chain.push({ el: panel, opener });
      panel.dataset.navDrillDepth = String(this.depth);

      const instant = this.shouldUseInstant();

      const reveal = () => {
        if (instant) {
          panel.classList.add('is-visible', 'menu-drawer-drill-panel--reduced');
        } else {
          panel.classList.add('is-visible');
        }
      };

      if (instant) {
        requestAnimationFrame(reveal);
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            void panel.offsetHeight;
            reveal();
          });
        });
      }

      queueMicrotask(() => {
        if (backBtn instanceof HTMLElement) backBtn.focus();
      });

      panel.addEventListener('click', (e) => {
        const push = resolveDrillPushButton(e.target);
        if (!push) return;
        const tplId = push.dataset.drillTemplate?.trim();
        if (!tplId) return;
        const tpl = document.getElementById(tplId);
        if (!(tpl instanceof HTMLTemplateElement)) return;
        const nextTitle = push.dataset.drillTitle?.trim() || '';
        const frag = document.importNode(tpl.content, true);
        this.push(nextTitle, frag, push);
      });
    }

    pop() {
      const last = this.chain.pop();
      if (!last) return;
      const { el, opener } = last;

      const done = () => {
        el.remove();
        this._deactivateIfEmpty();
        if (opener && typeof opener.focus === 'function') opener.focus();
      };

      if (this.shouldUseInstant()) {
        el.classList.remove('is-visible');
        done();
        return;
      }

      el.classList.add('is-leaving');
      el.classList.remove('is-visible');
      const onEnd = (ev) => {
        if (ev.target !== el || ev.propertyName !== 'transform') return;
        el.removeEventListener('transitionend', onEnd);
        done();
      };
      el.addEventListener('transitionend', onEnd);
      window.setTimeout(() => {
        if (el.isConnected) done();
      }, STRIP_MS + 80);
    }
  }

  /**
   * Drill row chevron, or label-only title (Dresses, etc. inside cloned template panels).
   * @param {EventTarget | null} target
   * @returns {HTMLButtonElement | null}
   */
  function resolveDrillPushButton(target) {
    if (!(target instanceof Element)) return null;
    const direct = target.closest('[data-nav-drill-push]');
    if (direct instanceof HTMLButtonElement) return direct;
    const label = target.closest('.nav-drawer-drill-push__label-only');
    if (!(label instanceof HTMLElement)) return null;
    const toggle = label.closest('.nav-drawer-drill-push')?.querySelector('[data-nav-drill-push]');
    return toggle instanceof HTMLButtonElement ? toggle : null;
  }

  /**
   * One delegated listener on .menu-drawer__navigation — covers drill rows cloned from
   * <template> (init-time querySelector misses those nodes).
   */
  function initDrawerLabelDelegation(root) {
    const navs = [];
    if (root instanceof Document) {
      document.querySelectorAll('.menu-drawer__navigation').forEach((n) => navs.push(n));
    } else if (root instanceof HTMLElement) {
      const nav = root.closest('.menu-drawer__navigation') || root.querySelector('.menu-drawer__navigation');
      if (nav) navs.push(nav);
    }

    for (const nav of navs) {
      if (!(nav instanceof HTMLElement) || nav.dataset.navDrawerLabelDelegationInit === '1') continue;
      nav.dataset.navDrawerLabelDelegationInit = '1';

      /** @param {HTMLElement} label @param {Event} e */
      const activateFromLabel = (label, e) => {
        if (label.closest('a[data-nav-drill-navigate], a[data-nav-drawer-row-link]')) return false;

        const stripHeader = label.closest('.nav-drawer-strip-row__header');
        if (stripHeader) {
          const toggle = stripHeader
            .closest('[data-nav-drawer-strip-row]')
            ?.querySelector('.nav-drawer-strip-row__toggle');
          if (!(toggle instanceof HTMLButtonElement)) return false;
          e.preventDefault();
          toggle.click();
          return true;
        }

        const drillRow = label.closest('.nav-drawer-drill-open-row');
        if (drillRow) {
          const toggle = drillRow.querySelector('[data-nav-drill-open]');
          if (!(toggle instanceof HTMLButtonElement)) return false;
          e.preventDefault();
          toggle.click();
          return true;
        }

        const inPanelBody = label.closest('.menu-drawer-drill-panel__body');
        /* Click: panel listener handles nested rows (avoid opening two panels). */
        if (inPanelBody && e.type === 'click') return false;

        const push = resolveDrillPushButton(label);
        if (!push) return false;
        e.preventDefault();
        push.click();
        return true;
      };

      nav.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;
        if (e.target.closest('a[data-nav-drill-navigate], a[data-nav-drawer-row-link]')) return;
        const label = e.target.closest(
          '.nav-drawer-drill-push__label-only, .nav-drawer-drill-open-row__label-only, .nav-drawer-strip-row__label-only'
        );
        if (!(label instanceof HTMLElement)) return;
        activateFromLabel(label, e);
      });

      nav.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (!(e.target instanceof Element)) return;
        const label = e.target.closest(
          '.nav-drawer-drill-push__label-only, .nav-drawer-drill-open-row__label-only, .nav-drawer-strip-row__label-only'
        );
        if (!(label instanceof HTMLElement)) return;
        if (activateFromLabel(label, e)) e.preventDefault();
      });
    }
  }

  /** @param {HTMLElement} stackEl */
  function getDrillStack(stackEl) {
    let c = drillStacks.get(stackEl);
    if (!c) {
      c = new DrillStack(stackEl);
      drillStacks.set(stackEl, c);
    }
    return c;
  }

  /** @param {Document | HTMLElement} root */
  function initDrillOpeners(root) {
    for (const btn of root.querySelectorAll('[data-nav-drill-open]')) {
      if (btn.dataset.navDrillOpenInit === '1') continue;
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.dataset.navDrillOpenInit = '1';

      btn.addEventListener('click', () => {
        const stackId = btn.dataset.drillStack?.trim();
        const tplId = btn.dataset.drillTemplate?.trim();
        if (!stackId || !tplId) return;
        const stackEl = document.getElementById(stackId);
        if (!(stackEl instanceof HTMLElement)) return;
        const tpl = document.getElementById(tplId);
        if (!(tpl instanceof HTMLTemplateElement)) return;
        const title = btn.dataset.drillTitle?.trim() || '';
        const frag = document.importNode(tpl.content, true);
        getDrillStack(stackEl).push(title, frag, btn);
      });
    }
  }

  /** When the title has no URL, tapping the label opens strip / drill (same as the chevron). */
  function initDrawerRowNoLinkLabels(root) {
    for (const row of root.querySelectorAll('.nav-drawer-drill-open-row')) {
      const label = row.querySelector('.nav-drawer-drill-open-row__label-only');
      const toggle = row.querySelector('[data-nav-drill-open]');
      if (!(label instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) continue;
      if (label.dataset.navDrawerNoLinkInit === '1') continue;
      label.dataset.navDrawerNoLinkInit = '1';
      label.setAttribute('role', 'button');
      if (!label.hasAttribute('tabindex')) label.setAttribute('tabindex', '0');
      const open = () => toggle.click();
      label.addEventListener('click', open);
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      });
    }

    for (const row of root.querySelectorAll('[data-nav-drawer-strip-row]')) {
      const header = row.querySelector('.nav-drawer-strip-row__header');
      const toggle = row.querySelector('.nav-drawer-strip-row__toggle');
      if (!(header instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) continue;
      if (header.querySelector('a[data-nav-drawer-row-link]')) continue;
      const label = header.querySelector(
        '.nav-drawer-strip-row__label-only, .navigation-block__summary-text:not([data-nav-drawer-row-link])'
      );
      if (!(label instanceof HTMLElement) || label.tagName === 'A') continue;
      if (label.dataset.navDrawerNoLinkInit === '1') continue;
      label.dataset.navDrawerNoLinkInit = '1';
      label.setAttribute('role', 'button');
      if (!label.hasAttribute('tabindex')) label.setAttribute('tabindex', '0');
      const open = () => toggle.click();
      label.addEventListener('click', open);
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      });
    }
  }

  /** Nested drill rows: label-only title opens sub-panel (same as chevron). */
  function initDrillPushNoLinkLabels(root) {
    for (const push of root.querySelectorAll('.nav-drawer-drill-push')) {
      const label = push.querySelector('.nav-drawer-drill-push__label-only');
      const toggle = push.querySelector('[data-nav-drill-push]');
      if (!(label instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) continue;
      if (label.dataset.navDrawerNoLinkInit === '1') continue;
      label.dataset.navDrawerNoLinkInit = '1';
      label.setAttribute('role', 'button');
      if (!label.hasAttribute('tabindex')) label.setAttribute('tabindex', '0');
      const open = () => toggle.click();
      label.addEventListener('click', open);
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      });
    }
  }

  /** Top-level drawer rows: title link navigates; chevron opens strip / drill only. */
  function initDrawerRowNavigateLinks(root) {
    for (const a of root.querySelectorAll('a[data-nav-drawer-row-link]')) {
      if (!(a instanceof HTMLAnchorElement)) continue;
      if (a.dataset.navDrawerRowLinkInit === '1') continue;
      a.dataset.navDrawerRowLinkInit = '1';
      a.addEventListener('click', (e) => {
        if (!a.closest('header-drawer')) return;
        const href = a.getAttribute('href');
        if (!href || href === '#' || href.startsWith('javascript:')) return;
        e.preventDefault();
        closeHeaderDrawer();
        window.setTimeout(() => {
          window.location.assign(href);
        }, 0);
      });
    }
  }

  function initStoresLinks(root) {
    for (const a of root.querySelectorAll('a[data-nav-drawer-stores-link]')) {
      if (!(a instanceof HTMLAnchorElement)) continue;
      if (a.dataset.navStoresInit === '1') continue;
      a.dataset.navStoresInit = '1';
      a.addEventListener('click', (e) => {
        if (!a.closest('header-drawer')) return;
        e.preventDefault();
        const href = a.getAttribute('href') || '/pages/stores';
        closeHeaderDrawer();
        window.setTimeout(() => {
          window.location.assign(href);
        }, 0);
      });
    }
  }

  function initDrillNavigateLinks(root) {
    for (const stack of root.querySelectorAll('.menu-drawer-drill-stack')) {
      if (stack.dataset.navDrillNavigateInit === '1') continue;
      stack.dataset.navDrillNavigateInit = '1';
      stack.addEventListener('click', (e) => {
        const a = e.target instanceof Element ? e.target.closest('a[data-nav-drill-navigate]') : null;
        if (!(a instanceof HTMLAnchorElement)) return;
        const href = a.getAttribute('href');
        if (!href || href === '#' || href === '/#' || href.startsWith('javascript:')) return;
        if (!a.closest('header-drawer')) return;
        e.preventDefault();
        closeHeaderDrawer();
        window.setTimeout(() => {
          window.location.assign(href);
        }, 0);
      });
    }
  }

  function initLucidePlaceholders() {
    if (window.__navDrawerLucideChevronLeft) return;
    window.__navDrawerLucideChevronLeft =
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide-icon" aria-hidden="true" focusable="false"><path d="m15 18-6-6 6-6"/></svg>';
  }

  /** @type {WeakMap<HTMLElement, number>} */
  const stripScrollMax = new WeakMap();

  /** @param {HTMLElement} track */
  function syncStripTrack(track) {
    const strip = track.closest('[data-strip-product-count]');
    const count = strip instanceof HTMLElement ? strip.dataset.stripProductCount : '';
    const grid = track.querySelector('.mega-menu-product-strip__grid');
    if (!(grid instanceof HTMLElement)) return;

    if (count === '1' || count === '2') {
      grid.style.removeProperty('width');
      grid.style.removeProperty('max-width');
      stripScrollMax.delete(track);
      if (track.scrollLeft !== 0) track.scrollLeft = 0;
      return;
    }

    const cells = grid.querySelectorAll('.mega-menu-product-strip__cell');
    const last = cells[cells.length - 1];
    if (!(last instanceof HTMLElement)) return;

    const lastRight = last.offsetLeft + last.offsetWidth;
    grid.style.width = `${lastRight}px`;
    grid.style.maxWidth = `${lastRight}px`;

    if (track.scrollWidth <= track.clientWidth + 1) {
      stripScrollMax.set(track, 0);
      if (track.scrollLeft !== 0) track.scrollLeft = 0;
      return;
    }

    const max = Math.max(0, lastRight - track.clientWidth);
    stripScrollMax.set(track, max);
    if (track.scrollLeft > max + 1) track.scrollLeft = max;
  }

  /** @param {HTMLElement} track — scroll-path only: clamps using cached max, no layout reads */
  function clampStripScroll(track) {
    const max = stripScrollMax.get(track);
    if (max === undefined) return;
    if (track.scrollLeft > max + 1) track.scrollLeft = max;
  }

  /** @param {Document | HTMLElement} root */
  function initStripScrollClamp(root) {
    const from = root instanceof Document ? document.body : root;
    if (!(from instanceof HTMLElement)) return;

    for (const track of from.querySelectorAll(
      '.menu-drawer__navigation .mega-menu-product-strip__track-outer'
    )) {
      if (!(track instanceof HTMLElement)) continue;

      const strip = track.closest('[data-strip-product-count]');
      const count = strip instanceof HTMLElement ? strip.dataset.stripProductCount : '';
      if (count === '1' || count === '2') continue;

      if (track.dataset.stripScrollClamp !== '1') {
        track.dataset.stripScrollClamp = '1';
        track.addEventListener('scroll', () => clampStripScroll(track), { passive: true });
        track.addEventListener('scrollend', () => clampStripScroll(track), { passive: true });
        navStripResizeObserver?.observe(track);
        for (const img of track.querySelectorAll('.mega-menu-product-strip__img')) {
          if (!(img instanceof HTMLImageElement) || img.complete) continue;
          img.addEventListener('load', () => syncStripTrack(track), { once: true });
        }
      }

      requestAnimationFrame(() => syncStripTrack(track));
    }
  }

  /** @param {Document | HTMLElement} root */
  function init(root) {
    initLucidePlaceholders();
    hoistDrillStacks(root);
    initStripRows(root);
    initStripScrollClamp(root);
    initDrillOpeners(root);
    initDrawerLabelDelegation(root);
    initDrawerRowNavigateLinks(root);
    initStoresLinks(root);
    initDrillNavigateLinks(root);
  }

  /** Shared ResizeObserver: one instance for all tracks, avoids per-track observer leaks. */
  const navStripResizeObserver = (() => {
    if (typeof ResizeObserver === 'undefined') return null;
    if (!window.__navDrawerStripRO) {
      window.__navDrawerStripRO = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target instanceof HTMLElement) syncStripTrack(entry.target);
        }
      });
    }
    return /** @type {ResizeObserver} */ (window.__navDrawerStripRO);
  })();

  /** Single shared window resize handler — avoids one listener per track on the global object. */
  if (!window.__navDrawerStripResizeSync) {
    window.__navDrawerStripResizeSync = true;
    window.addEventListener(
      'resize',
      () => {
        for (const track of document.querySelectorAll(
          '.menu-drawer__navigation .mega-menu-product-strip__track-outer[data-strip-scroll-clamp="1"]'
        )) {
          if (track instanceof HTMLElement) syncStripTrack(track);
        }
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  if (!window.__navDrawerStripOpenSync) {
    window.__navDrawerStripOpenSync = true;
    document.addEventListener(
      'toggle',
      (e) => {
        const details = e.target;
        if (!(details instanceof HTMLDetailsElement) || !details.closest('header-drawer')) return;
        if (!details.hasAttribute('open')) return;
        requestAnimationFrame(() => {
          for (const track of document.querySelectorAll(
            '.menu-drawer__navigation .mega-menu-product-strip__track-outer'
          )) {
            if (track instanceof HTMLElement) syncStripTrack(track);
          }
        });
      },
      true
    );
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target instanceof HTMLElement) init(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    if (!(e.target instanceof HTMLElement) || !window.__navDrawerStripRO) return;
    for (const track of e.target.querySelectorAll('.mega-menu-product-strip__track-outer')) {
      if (track instanceof HTMLElement) window.__navDrawerStripRO.unobserve(track);
    }
  });
})();
